/* eslint-disable no-nested-ternary */
import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Button, LinkButton} from '@components/common/base/buttons'
import {Input} from '@components/common/base/input'
import {
  useGlobalKeyDown,
  useInputKeyDown,
  useScrollState
} from '@containers/hooks'
import {useProjectParticipantStore} from './project-participants-store'
import {RolesTab} from './roles-tab'
import {UsersTab} from './users-tab'
import {UsersTabTableHeader} from './users-tab-table-header'

const TwCaption = tw.div`
  h2-semibold
  text-primary
  cursor-default
`
const TwCaption2 = tw.div`
  text-sm-regular
  text-secondary
  cursor-default
`

export const UsersRolesTabContent = observer(() => {
  const [state, store] = useProjectParticipantStore()

  const projInputNameRef = useRef<HTMLInputElement>()

  const [listRef, listTop] = useScrollState()
  const bem = Util.bemFrom('project-participants')

  useGlobalKeyDown({
    // TODO not worked escape
    escape: () => {
      store.closeWithWarn()
      return true
    },
    enter: () => {
      store.submit()
      return true
    }
  })

  useInputKeyDown({
    ref: projInputNameRef,
    save: store.submit,
    cancel: store.closeWithWarn
  })

  const projPrefixError = state.projPrefixError

  return (
    <>
      {store.props.project && (
        <div className="pt-8 mb-32 flex flex-col gap-8">
          <TwCaption>{Lang.msg.invitePopup.inviteCaption}</TwCaption>
          <div className="flex items-center gap-8">
            <Input
              onClick={() => {
                store.selectInviteTab()
                void Tracking.send(
                  TrackingEvent.projectRoleManagementPopupInviteByEmailInputActivation
                )
              }}
              placeholder={Lang.msg.invitePopup.inputPlaceholderShort}
            />

            <Button
              color="primary"
              onClick={() => {
                store.selectInviteTab()
                void Tracking.send(
                  TrackingEvent.projectRoleManagementPopupInviteButtonActivation
                )
              }}
              rounded={'8'}
            >
              {Lang.msg.invitePopup.inviteButton}
            </Button>
          </div>
        </div>
      )}

      {store.isCreateProject && (
        <div className="flex flex-col gap-8 mb-24">
          <div className="flex items-center">
            <TwCaption>{Lang.msg.projectName}</TwCaption>
            <TwCaption2 className={'ml-auto w-220'}>
              {Lang.msg.proj.projectsIdTitle}
            </TwCaption2>
          </div>
          <div className="flex items-center gap-16">
            <Input
              ref={projInputNameRef}
              autoFocus
              onChange={store.setTitle}
              placeholder={Lang.msg.projectPlaceholder}
              value={state.title}
              className={'max-w-full'}
            />
            <div className={'w-220 relative'}>
              <Input
                onChange={store.setProjId}
                value={state.projId}
                isValid={() => (projPrefixError ? 'error' : 'empty')}
              />
              {projPrefixError && (
                <div className="text-status-error micro-regular absolute top-42 left-0 cursor-default">
                  {Lang.msg.idProjectBusy}
                </div>
              )}
              {state.projPrefixInvalid && (
                <div className="text-status-error micro-regular absolute top-42 left-0 cursor-default">
                  {Lang.msg.idProjectInvalid}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={'pb-8 flex items-center justify-between'}>
        {state.tab === 'users' ? (
          <TwCaption>{Lang.msg.proj.usersTab}</TwCaption>
        ) : (
          <LinkButton onClick={store.selectUsersTab} noPaddings>
            {Lang.msg.proj.usersTab}
          </LinkButton>
        )}

        {store.rolesSupported && state.tab === 'roles' ? (
          <TwCaption>{Lang.msg.proj.rolesTab}</TwCaption>
        ) : (
          <LinkButton
            onClick={() => {
              store.selectRolesTab()
              void Tracking.send(
                TrackingEvent.userInvitationPopupInviteRoleSetupExpansion
              )
            }}
            noPaddings
          >
            {Lang.msg.proj.rolesTab}
          </LinkButton>
        )}
      </div>

      {store.isUsersTab && <UsersTabTableHeader />}
      <div className={bem('shadow', {scrolled: !listTop})} />
      <div className={bem('list')} ref={listRef}>
        {store.isUsersTab && <UsersTab />}
        {store.isRolesTab && <RolesTab />}
      </div>
    </>
  )
})
